/**
 * Open/closed badge on the store detail page.
 *
 * The `hours` metaobject field is free text ("Lundi – Vendredi : 11:00 – 19:00"), so the ranges are
 * parsed from what is rendered. Parsing is deliberately tolerant — day names in any case or accent,
 * hyphen/en-dash/em-dash, "11:00" or "11h" — and deliberately silent: if nothing parses, no badge is
 * shown rather than a wrong "closed". A store that opens at all is assumed closed on any day the
 * text does not mention.
 *
 * The field is translated, so the same text arrives in French, English and German (JDTMA-763):
 * "Monday to Saturday: 11:00 AM - 7:00 PM", "Montag bis Samstag : 11:00 - 19:00". Day names, the
 * "to"/"bis" range word, the "and"/"und" list word, the "closed"/"geschlossen" keyword and the
 * English 12-hour clock are all handled below. Anything else still falls back to no badge.
 */
(() => {
  // Day-name prefixes of the three contributed locales, each mapped to its weekday index (0 = Sunday).
  // The cross-language collisions are all benign: "mon" is Monday in English and German, "sam"
  // Saturday in French and German. No prefix is a prefix of another, so order doesn't matter.
  const DAY_PREFIXES = [
    ['dim', 0], ['sun', 0], ['son', 0],
    ['lun', 1], ['mon', 1],
    ['mar', 2], ['tue', 2], ['die', 2],
    ['mer', 3], ['wed', 3], ['mit', 3],
    ['jeu', 4], ['thu', 4], ['don', 4],
    ['ven', 5], ['fri', 5], ['fre', 5],
    ['sam', 6], ['sat', 6],
  ];
  const EN_WEEKDAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const RANGE_SEPARATOR = /\s(?:-|–|—|au?|to|bis)\s/i;
  const LIST_SEPARATOR = /\s*(?:,|\/|\bet\b|\band\b|\bund\b)\s*/i;
  // The [:h] separator stays required on purpose: it keeps a bare number in a sentence ("from Monday
  // 10 August 2026") from reading as a time. The meridiem is optional and only English uses it.
  const TIME = /(\d{1,2})\s*[:h]\s*(\d{2})?\s*(am|pm)?/gi;
  const CLOSED_WORDS = ['ferme', 'closed', 'geschlossen'];

  const normalize = (value) =>
    value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const dayIndex = (token) => {
    const clean = normalize(token);
    const match = DAY_PREFIXES.find(([prefix]) => clean.startsWith(prefix));
    return match ? match[1] : -1;
  };

  /** Expands "lundi – vendredi" or "samedi, dimanche" into day indexes. */
  const parseDays = (part) => {
    const range = part.split(RANGE_SEPARATOR);
    if (range.length === 2) {
      const from = dayIndex(range[0]);
      const to = dayIndex(range[1]);
      if (from !== -1 && to !== -1) {
        const days = [];
        for (let day = from; ; day = (day + 1) % 7) {
          days.push(day);
          if (day === to) break;
        }
        return days;
      }
    }
    return part
      .split(LIST_SEPARATOR)
      .map(dayIndex)
      .filter((day) => day !== -1);
  };

  /** One line -> { days, start, end } in minutes, or null. */
  const parseLine = (line) => {
    TIME.lastIndex = 0;
    const times = [...line.matchAll(TIME)].map((match) => {
      let hours = parseInt(match[1], 10);
      const minutes = parseInt(match[2] || '0', 10);
      const meridiem = (match[3] || '').toLowerCase();
      if (meridiem === 'pm' && hours < 12) hours += 12;
      if (meridiem === 'am' && hours === 12) hours = 0;
      return hours * 60 + minutes;
    });

    const firstTime = line.search(/\d/);
    const daysPart = (firstTime === -1 ? line : line.slice(0, firstTime)).replace(/[:.]\s*$/, '');
    const days = parseDays(daysPart);
    if (!days.length) return null;

    if (times.length < 2) {
      const text = normalize(line);
      return CLOSED_WORDS.some((word) => text.includes(word)) ? { days, closed: true } : null;
    }
    return { days, start: times[0], end: times[1] };
  };

  /** Each line with the element it came from, so unparsed ones can be marked up. */
  const readLines = (container) => {
    const children = [...container.querySelectorAll('li, p')];
    const entries = children.length
      ? children.map((element) => ({ text: element.textContent, element }))
      : container.textContent.split('\n').map((text) => ({ text, element: null }));
    return entries
      .map((entry) => ({ ...entry, text: entry.text.trim() }))
      .filter((entry) => entry.text);
  };

  /** Current weekday, minutes-since-midnight and ISO date at the store, not in the visitor's zone. */
  const nowAt = (timeZone) => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      weekday: 'short',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date());

    const lookup = (type) => parts.find((part) => part.type === type)?.value;
    const day = EN_WEEKDAYS[lookup('weekday')];
    const hours = parseInt(lookup('hour'), 10) % 24;
    const minutes = parseInt(lookup('minute'), 10);
    if (day === undefined || Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    return {
      day,
      minutes: hours * 60 + minutes,
      date: `${lookup('year')}-${lookup('month')}-${lookup('day')}`,
    };
  };

  /** ISO dates compare correctly as plain strings, so no Date parsing is involved. */
  const closureCovering = (closures, date) =>
    closures.find((closure) => date >= closure.from && date <= (closure.to || closure.from));

  const readClosures = (badge) => {
    try {
      const parsed = JSON.parse(badge.dataset.closures || '[]');
      return Array.isArray(parsed) ? parsed.filter((entry) => entry && entry.from) : [];
    } catch {
      return [];
    }
  };

  const isOpen = (ranges, now) => {
    const yesterday = (now.day + 6) % 7;
    return ranges.some((range) => {
      if (range.closed) return false;
      const spansMidnight = range.end <= range.start;
      if (range.days.includes(now.day)) {
        if (spansMidnight ? now.minutes >= range.start : now.minutes >= range.start && now.minutes < range.end) {
          return true;
        }
      }
      // A range that runs past midnight keeps yesterday's entry open into today.
      return spansMidnight && range.days.includes(yesterday) && now.minutes < range.end;
    });
  };

  const render = (badge) => {
    const block = badge.closest('.jd-store-detail__block');
    const container = block && block.querySelector('[data-jd-store-hours]');
    if (!container) return;

    const ranges = [];
    const notes = [];
    for (const entry of readLines(container)) {
      const parsed = parseLine(entry.text);
      // Anything that isn't an opening range is an exceptional note typed inside `hours`
      // ("Fermeture estivale du…"), which the badge can only act on through `closures`.
      if (parsed) ranges.push(parsed);
      else if (entry.element) notes.push(entry.element);
    }
    if (!ranges.some((range) => !range.closed)) return;

    let now;
    try {
      now = nowAt(badge.dataset.timezone || 'Europe/Paris');
    } catch {
      return;
    }
    if (!now) return;

    const closures = readClosures(badge);
    const open = !closureCovering(closures, now.date) && isOpen(ranges, now);
    badge.textContent = open ? badge.dataset.labelOpen : badge.dataset.labelClosed;
    badge.dataset.open = String(open);
    badge.hidden = false;

    // A note is only flagged where the status is genuinely a guess. Once `closures` is filled the
    // badge accounts for the closure, so the merchant's sentence reads as plain information.
    if (closures.length === 0) {
      notes.forEach((element) => element.classList.add('jd-store-detail__hours-exception'));
    }
  };

  document.querySelectorAll('[data-jd-store-status]').forEach(render);
})();
